// dashboard/frontend/src/components/Campaign/channels/email/EmailDraftActions.js

import React from 'react';
import { IconButton, Tooltip, Box, Typography } from '@mui/material';
import {
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    Email as EmailIcon,
} from '@mui/icons-material';

/**
 * Email-specific actions for draft rows.
 * - No draft yet: Generate button.
 * - Draft (not sent): View / View-in-Gmail / Regenerate / Delete.
 * - Sent: open-tracking stats instead of buttons (no preview, no delete).
 */
const EmailDraftActions = ({
    draft,
    contact,
    hasChannelInfo,
    expanded,
    setExpanded,
    onViewInGmail,
    onRefreshDraft,
    onDeleteDraft,
}) => {
    // No draft yet - show generate button if contact has email
    if (!draft) {
        if (hasChannelInfo && onRefreshDraft) {
            return (
                <Tooltip title="Generate draft">
                    <IconButton size="small" onClick={() => onRefreshDraft(contact.id)}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            );
        }
        return null;
    }

    // Bounced - the bounce poller found a hard bounce for this send
    if (draft.status === 'bounced') {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 170 }}>
                <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
                    Bounced
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Sent: {draft.sent_at ? new Date(draft.sent_at).toLocaleString() : '—'}
                </Typography>
                {draft.error_message && (
                    <Tooltip title={draft.error_message}>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
                            {draft.error_message}
                        </Typography>
                    </Tooltip>
                )}
                {onDeleteDraft && (
                    <Box sx={{ mt: 0.25 }}>
                        <Tooltip title="Delete sent record">
                            <IconButton size="small" color="error" onClick={() => onDeleteDraft(draft.id)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        );
    }

    // Sent - replace action buttons with open-tracking stats
    if (draft.status === 'sent') {
        const opens = draft.open_count || 0;
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 170 }}>
                <Typography variant="caption" color="text.secondary">
                    Sent: {draft.sent_at ? new Date(draft.sent_at).toLocaleString() : '—'}
                </Typography>
                <Typography variant="caption" color={opens ? 'success.main' : 'text.secondary'}>
                    Opens: {opens}
                    {opens > 0 && draft.human_open_count != null
                        ? ` (${draft.human_open_count} likely human)`
                        : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {draft.first_opened_at
                        ? `First open: ${new Date(draft.first_opened_at).toLocaleString()}`
                        : 'Not opened yet'}
                </Typography>
                {opens > 1 && draft.last_opened_at && (
                    <Typography variant="caption" color="text.secondary">
                        Last open: {new Date(draft.last_opened_at).toLocaleString()}
                    </Typography>
                )}
                {onDeleteDraft && (
                    <Box sx={{ mt: 0.25 }}>
                        <Tooltip title="Delete sent record">
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => onDeleteDraft(draft.id)}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        );
    }

    // Draft exists but not sent - editing actions
    return (
        <>
            {/* View/Hide button */}
            <Tooltip title={expanded ? "Hide" : "View"}>
                <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                    {expanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
            </Tooltip>

            {/* View in Gmail button */}
            {onViewInGmail && (
                <Tooltip title="View in Gmail">
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onViewInGmail(contact.email)}
                    >
                        <EmailIcon />
                    </IconButton>
                </Tooltip>
            )}

            {/* Regenerate button */}
            {onRefreshDraft && (
                <Tooltip title="Regenerate">
                    <IconButton size="small" onClick={() => onRefreshDraft(contact.id)}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            )}

            {/* Delete button (drafts only) */}
            {onDeleteDraft && (
                <Tooltip title="Delete draft">
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => onDeleteDraft(draft.id)}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            )}
        </>
    );
};

export default EmailDraftActions;